import type { Config, DownloadProgress, ProgressListener } from '~/composables/streaming-download'

export type ActiveDownload = DownloadProgress & {
  id: string
  fileName: string
  controller: AbortController
  error?: string
  isCancelled: boolean
  isFinished: boolean
  percentage: number
}
type DownloadConfig<T> = Omit<Config<T>, 'abortController'> & Partial<Pick<Config<T>, 'abortController'>>
/**
 * Downloads store - used to manage all active downloads and allow access to download status
 */
export const useDownloadsStore = defineStore('downloads', () => {
  const { isAvailable, download: executeDownload } = useStreamingDownload()
  const activeDownloads = ref<ActiveDownload[]>([])

  function clearActiveDownload(id: string) {
    const index = activeDownloads.value.findIndex(a => a.id === id)
    if (index >= 0) {
      activeDownloads.value.splice(index, 1)
    }
  }

  function download<T>(config: DownloadConfig<T>) {
    const abortController = config.abortController || new AbortController()
    const activeDownload: ActiveDownload = reactive({
      id: crypto.randomUUID(),
      fileName: config.fileName,
      totalSize: 0,
      downloaded: 0,
      percentage: 0,
      isCancelled: false,
      isFinished: false,
      controller: abortController,
    })
    abortController.signal.addEventListener('abort', () => {
      activeDownload.isCancelled = true
    })
    activeDownloads.value.push(activeDownload)
    const progressListener: ProgressListener = (progress) => {
      if (config.progressListener) {
        config.progressListener(progress)
      }
      activeDownload.totalSize = progress.totalSize
      activeDownload.downloaded = progress.downloaded
      activeDownload.percentage = (progress.downloaded * 100 / progress.totalSize)
      activeDownload.isFinished = (progress.downloaded === progress.totalSize)
    }
    return executeDownload<T>({ ...config, progressListener, abortController })
      .catch((e) => {
        console.log('Download error: ', e)
        if (!(e instanceof DOMException && e.name === 'AbortError')) {
          if (typeof e === 'string') {
            activeDownload.error = `An error occurred: ${e}`
          } else {
            activeDownload.error = `An error occurred during download`
          }
        }
      })
  }
  return {
    isAvailable,
    activeDownloads,
    download,
    clearActiveDownload,
  }
})
